<template>
	<div id="vuesoma">
		<router-view />
		<c-fullscreen-spinner
			v-if="status"
			data-testid="spinner"
		/>
		<w-modal />
	</div>
</template>

<script>
import { throttle } from 'lodash';
import { mapState, getActivePinia } from 'pinia';
import { importLocale } from '@plugins/i18n';
import CFullscreenSpinner from '@components/c-fullscreen-spinner.vue';
import WModal from '@widgets/w-modal.vue';
import MConfirm from '@modals/m-confirm';
import bugsnagClient from '@plugins/bugsnag';
import MNoInternet from '@fam/modals/m-no-internet.vue';

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const MAX_MINUTES_FOR_SESSION_EXPIRATION = 5;
const MAX_MINUTES_FOR_SESSION_EXPIRATION_ALERT = 4.5;
let ONCE = true;

const isPrd = window.VITE_APP_CONFIG?.env === 'prd';

export default {
	name: 'app',

	components: {
		CFullscreenSpinner,
		WModal,
	},

	data() {
		return {
			expiredSessionTimeout: 0,
			alertSessionTimeout: 0,
			expiredSessionMaxMinutes: MAX_MINUTES_FOR_SESSION_EXPIRATION,
			alertSessionMaxMinutes: MAX_MINUTES_FOR_SESSION_EXPIRATION_ALERT,
		};
	},

	computed: {
		...mapState(useAppStore, ['isEmbedded']),
		...mapState(useAuthStore, ['isLoggedIn']),
		...mapState(useSessionStore, ['theme', 'lang', 'userId']),
		...mapState(useLoadingStore, ['status']),
		...mapState(useServiceStore, ['lastRequestTimestamp', 'offline']),
		...mapState(useSecureStore, ['uuid']),
	},

	watch: {
		/* istanbul ignore next */
		isLoggedIn(value) {
			var cookieBotWidget = document.getElementById('CookiebotWidget');
			if (!value) {
				if (cookieBotWidget) {
					cookieBotWidget.style.display = 'inline';
				}
				clearInterval(this.expiredSessionTimeout);
				clearInterval(this.alertSessionTimeout);

				if (this.$route.name !== 'login') {
					this.$router.push({ name: 'login' });
				}
			} else {
				if (cookieBotWidget) {
					cookieBotWidget.style.display = 'none';
				}
				this.expiredSessionTimeout = setInterval(this.checkForSessionExpiration, ONE_SECOND);
				this.alertSessionTimeout = setInterval(this.alertForSessionAboutToExpire, ONE_SECOND);
			}
		},

		theme: {
			immediate: true,
			handler(value) {
				document.documentElement.dataset.theme = value;
			},
		},

		lang: {
			immediate: true,
			handler(value) {
				importLocale(value);
			},
		},

		/* istanbul ignore next */
		uuid(next, prev) {
			/* istanbul ignore else */
			if (prev && !next && window.parent) {
				window.parent.postMessage(
					{
						name: 'error',
						userId: this.userId,
						sessionUUID: prev,
						error: 'session-expired',
					},
					'*'
				);
			}
		},

		offline: {
			async handler(value) {
				if (value) {
					await useModalStore().open(MNoInternet);
					useServiceStore().setOffline(false);
				}
			},
		},
	},

	methods: {
		getMinutesFromLastRequest(lastRequestTimestamp) {
			return (new Date(Date.now()).getTime() - lastRequestTimestamp) / 1000 / 60;
		},

		async alertForSessionAboutToExpire() {
			const { lastRequestTimestamp, alertSessionMaxMinutes } = this;

			/* istanbul ignore else */
			if (this.getMinutesFromLastRequest(lastRequestTimestamp) > alertSessionMaxMinutes) {
				const props = {
					title: this.$t('INFO.SESSION_ABOUT_EXPIRE.TITLE'),
					text: this.$t('INFO.SESSION_ABOUT_EXPIRE.DESC'),
					acceptText: this.$t('ACTIONS.CONTINUE_SESSION'),
					cancelText: this.$t('ACTIONS.CLOSE_SESSION'),
					timeout: 60000,
				};

				clearInterval(this.alertSessionTimeout);

				const confirmation = await useModalStore().open({
					component: MConfirm,
					props,
				});

				if (confirmation === undefined) {
					return useAuthStore().passiveLogout();
				}

				// Refresh session if user clicks accept or closes the modal
				if (confirmation || confirmation === null) {
					this.alertSessionTimeout = setInterval(
						this.alertForSessionAboutToExpire,
						ONE_SECOND
					);

					return useAuthStore().refresh();
				}

				useAuthStore().logout();
			}
		},

		checkForSessionExpiration() {
			const { lastRequestTimestamp, expiredSessionMaxMinutes } = this;
			const minutesFromLastRequest =
				(new Date(Date.now()).getTime() - lastRequestTimestamp) / 1000 / 60;

			/* istanbul ignore else */
			if (minutesFromLastRequest > expiredSessionMaxMinutes) {
				clearInterval(this.expiredSessionTimeout);

				return useAuthStore().passiveLogout();
			}
		},

		handleRefresh() {
			if (this.isLoggedIn) {
				useAuthStore().refresh();
			}
		},
	},

	created() {
		/* istanbul ignore next */
		this.$router.afterEach((to, from) => {
			useBugsnagStore().log({
				type: 'navigation',
				title: 'Router push',
				from: from.path,
				to: to.path,
			});
		});

		window.dispatchEvent(new Event('app-about-to'));

		/* istanbul ignore next */
		if (!isPrd && !this.isEmbedded) {
			const { log } = console;

			[
				'native-hide-loading',
				'native-copy-text',
				'native-open-external-link',
				'native-save-file',
				'native-share-file',
				'native-exit-app',
				'native-open-whatsapp',
				'native-open-email',
				'native-open-telephone',
				'native-set-theme',
				'native-get-wallet-status',
				'native-get-wallet-state',
				'native-get-wallet-data',
				'native-add-card-to-wallet',
				'native-start-card-provisioning',
				'splashscreen-unload',
			].forEach((eventName) => {
				window.addEventListener(
					eventName,
					(event) => {
						log({
							type: event?.type,
							detail: { ...event?.detail },
						});
					},
					false
				);
			});

			window.addEventListener(
				'message',
				(event) => {
					if (event?.data?.name) {
						log({
							name: event.data.name,
							data: event?.data,
						});
					}
				},
				false
			);
		}

		this.handleThrottleRefresh = throttle(this.handleRefresh, ONE_MINUTE);
		window.addEventListener('click', this.handleThrottleRefresh, true);
		window.addEventListener('scroll', this.handleThrottleRefresh, true);
		window.addEventListener('keydown', this.handleThrottleRefresh, true);

		window.addEventListener('CookiebotOnDecline', () => {
			if (window.Cookiebot.changed) {
				window.location.reload();
			}
		});

		useBugsnagStore().install(bugsnagClient);

		window.addEventListener('offline', () => {
			useServiceStore().setOffline(true);
		});
		window.addEventListener('online', () => {
			useServiceStore().setOffline(false);
		});

		window.addEventListener('message', ({ data, source, origin }) => {
			if (data?.name === 'bridge-store-dispatch') {
				const store = {
					dispatch(action, payload) {
						const [storeName, methodName] = action.split('/'); // Separamos el store y la acción
						const realStoreName = `m-${storeName}`;
						const store = getActivePinia()._s.get(realStoreName);
						if (typeof store[methodName] === 'function') {
							store[methodName](payload);
						}
					},
				};
				return store
					.dispatch(data.action, data.payload)
					.then((response) => {
						const message = {
							name: 'bridge-store-response',
							request: {
								action: data.action,
								payload: data.payload,
							},
							response: JSON.parse(JSON.stringify(response)),
						};

						return message;
					})
					.catch((error) => {
						const message = {
							name: 'bridge-store-error',
							request: {
								action: data.action,
								payload: data.payload,
							},
							response: error,
						};

						return message;
					})
					.then((message) => {
						ports?.[0]?.postMessage(message);
						window.postMessage(message, '*');
					});
			}

			if (data?.name === 'bridge-network-request') {
				return useServiceStore()
					.request(data.payload)
					.then(({ data: response }) => {
						const message = {
							name: 'bridge-network-response',
							request: data.payload,
							response: JSON.parse(JSON.stringify(response)),
						};

						return message;
					})
					.catch(({ response }) => {
						const message = {
							name: 'bridge-network-error',
							request: data.payload,
							response: JSON.parse(JSON.stringify(response)),
						};

						return message;
					})
					.then((message) => {
						ports?.[0]?.postMessage(message);
						window.postMessage(message, '*');
					});
			}

			if (data?.name === 'create-session') {
				useAppStore().setIsEmbedded();

				return useAuthStore().createUserSession({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'new-update') {
				const userUUID = useSecureStore().getUuid;

				if (window.parent) {
					window.parent.postMessage(
						{
							name: 'reload',
							userUUID,
						},
						'*'
					);
				}
			}

			if (data?.name === 'request-access') {
				const isDocumentManagement = data?.documentManagement;
				return useAuthStore()
					.authorizeAccess({
						data,
						source,
						origin,
						chooseOwnerContract: isDocumentManagement,
					})
					.then(() => this.$router.push({ name: 'products' }));
			}

			if (data?.name === 'get-notification-mode') {
				return useUserStore().getNotificationMode({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'change-notification-mode') {
				return useUserStore().changeNotificationMode({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'set-push-notification') {
				return useUserStore().setPushNotificationState({
					activated: data?.activated,
				});
			}

			if (data?.name === 'modify-user-device') {
				return useUserStore().modifyUserDevice({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'get-user-devices') {
				return useUserStore().getAssistedDevices({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'get-user-messages') {
				const paginationKey = data.payload;
				return useUserStore().getAssistedUserMessages({
					data,
					source,
					origin,
					paginationKey,
				});
			}

			if (data?.name === 'get-user-sessions') {
				const paginationKey = data.payload;
				return useUserStore().getAssistedUserSessions({
					data,
					source,
					origin,
					paginationKey,
				});
			}

			if (data?.name === 'get-session-detail') {
				const sessionDetailId = data.payload;
				return useUserStore().getAssistedUserSessions({
					data,
					source,
					origin,
					sessionId: sessionDetailId,
				});
			}

			if (data?.name === 'get-signature-detail') {
				const signatureDetailId = data.payload;
				return useSignaturesStore().getAssistedUserSignatureDetail({
					data,
					source,
					origin,
					signatureId: signatureDetailId,
				});
			}

			if (data?.name === 'get-user-signatures') {
				const paginationKey = data.payload;
				return useSignaturesStore().getAssistedUserSignatures({
					data,
					source,
					origin,
					paginationKey,
				});
			}

			if (data?.name === 'get-session-signatures') {
				const { paginationKey, sessionUuid } = data.payload;
				return useSignaturesStore().getAssistedSessionSignatures({
					data,
					source,
					origin,
					paginationKey,
					sessionUuid,
				});
			}

			if (data?.name === 'request-option') {
				return useUserStore().requestOption({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'sirvase-request') {
				const params = data.payload;
				return useUserStore().getSirvaseRequests({
					data,
					source,
					origin,
					...params,
				});
			}

			if (data?.name === 'get-user-documents') {
				const params = data.payload;
				return useDocumentsStore().getAssistedUserDocuments({
					data,
					source,
					origin,
					...params,
				});
			}

			if (data?.name === 'post-user-document') {
				const params = data.payload;
				return useDocumentsStore().postAssistedUserDocument({
					data,
					source,
					origin,
					...params,
				});
			}
			if (data?.name === 'post-user-document-request') {
				const params = data.payload;
				return useDocumentsStore().postAssistedUserDocument({
					data,
					source,
					origin,
					...params,
				});
			}

			if (data?.name === 'get-products') {
				return useProductsStore().getAssistedProducts({
					data,
					source,
					origin,
				});
			}

			if (data?.name === 'keep-alive') {
				return useAuthStore().refresh({
					skipLogout: false,
					updateRSISession: false,
				});
			}
			if (data?.command === 'native_download') {
				const downloadUrl = data?.payload?.url;
				if (!downloadUrl || !downloadUrl.startsWith('https://chat-server.mediquo.com/')) return;

				usePublicServiceStore()
					.request({
						url: downloadUrl,
						method: 'GET',
						responseType: 'blob',
					})
					.then((response) => {
						const blob = response.data;
						const type = blob.type;

						return new Promise((resolve, reject) => {
							const reader = new FileReader();

							reader.onloadend = () => {
								const result = reader.result;
								const b64Data = result.split(',')[1];
								resolve({ b64Data, type });
							};

							reader.onerror = reject;
							reader.readAsDataURL(blob);
						});
					})
					.then(({ b64Data, type }) => {
						window.dispatchEvent(
							new CustomEvent('native-save-file', {
								detail: { b64Data, type },
							})
						);
					})
					.catch((err) => {
						console.error('Native file download failed:', err);
					});
			}
		});

		if (window.parent) {
			window.parent.postMessage({ name: 'vuesoma-is-ready' }, '*');
		}

		window.postMessage({ name: 'vuesoma-is-ready' }, '*');
	},

	updated() {
		this.$nextTick(() => {
			// When skyline is embedded sometimes the frame is mounted
			// but it is not visible so the splash transition can't be listened
			// to fix it we remove the splash screen after the first update
			if (ONCE && this.isEmbedded && document.getElementById('splash')) {
				document.getElementById('splash')?.remove();
				ONCE = false;
			}
		});
	},

	mounted() {
		// We try to guarantee that all child components have been mounted
		// and the entire view has been rendered
		this.$nextTick(() => {
			window.dispatchEvent(new Event('app-is-mounted'));
		});
	},
};
</script>

<style lang="scss">
@use '@design/index.scss';

#vuesoma {
	display: flex;
	height: 100%;
}

[inert] {
	overflow: hidden;
}

.v__internal-a11y-hide {
	position: absolute;
	clip: rect(1px, 1px, 1px, 1px);
	background: 0 0;
	color: transparent;
	width: 1px;
	height: 1px;
	overflow: hidden;
}
:not([tabindex='-1']):not([type='radio']):focus:focus-visible::before {
	content: '';
	display: block;
	position: absolute;
	border-radius: var(--focus-ring-radius, inherit);
	top: calc(-1 * var(--focus-ring-padding, 0px) - 1px);
	bottom: calc(-1 * var(--focus-ring-padding, 0px) - 1px);
	left: calc(-1 * var(--focus-ring-padding, 0px) - 1px);
	right: calc(-1 * var(--focus-ring-padding, 0px) - 1px);
	border: 1px solid RGB(var(--focus-ring-color, var(--color-secondary)));
	box-shadow: 0 0 0 1px RGB(var(--focus-ring-color, var(--color-secondary)));
}
</style>
